/**
 * Split.io flag lookups backed by the shared session client from
 * app/plugins/split.client.ts (one SDK boot per session, re-keyed on
 * login/logout). Per the feature-flags standard, an SDK timeout resolves
 * flags to `false` (off) rather than rejecting.
 *
 * Example: isFlagOn(FEATURE_FLAG.pwa)
 */

/**
 * Resolves whether a single flag is on.
 * @param {string} key — a FEATURE_FLAG / KILL_SWITCH constant
 * @returns {Promise<boolean>}
 */
export default async function isFlagOn (key) {
  if (import.meta.server) return false
  const { $split } = useNuxtApp()
  // Missing manager (server-only app context, stubbed nuxt app in tests) —
  // default to off, mirroring the SDK-timeout behaviour.
  if (!$split) return false
  const { client, ready } = $split.getActive()
  if (!(await ready)) return false
  return client.getTreatment(key) === 'on'
}

/**
 * Resolves several flags in one SDK lookup.
 * @param {string[]} keys — FEATURE_FLAG / KILL_SWITCH constants
 * @returns {Promise<Record<string, boolean>>} map of key → on/off
 */
export async function areFlagsOn (keys) {
  const allOff = () => Object.fromEntries(keys.map(key => [key, false]))
  if (import.meta.server) return allOff()
  const { $split } = useNuxtApp()
  if (!$split) return allOff()
  const { client, ready } = $split.getActive()
  if (!(await ready)) return allOff()
  const treatments = client.getTreatments(keys)
  return Object.fromEntries(keys.map(key => [key, treatments[key] === 'on']))
}
